/**
 * Browser-side search index loader.
 *
 * Pulls `/search-index/<country>-<locale>.json` once per (country, locale)
 * and memoizes it. Returns a stable handle the modal/results component
 * uses to call `engine.search()`. No revalidation — the index is rebuilt
 * on every deploy and ships static under `public/`, so the file URL is
 * effectively immutable per release. The browser's HTTP cache + Vercel's
 * edge cache handle freshness.
 */

import { search, type SearchOptions, type SearchHit } from "./engine"
import type { SearchIndex, SearchDoc, SearchDocType } from "./types"
import { SEARCH_INDEX_VERSION } from "./types"

const cache = new Map<string, Promise<SearchIndex | null>>()

function indexUrl(country: string, locale: string): string {
  return `/search-index/${country}-${locale}.json`
}

async function fetchIndex(
  country: string,
  locale: string
): Promise<SearchIndex | null> {
  try {
    const res = await fetch(indexUrl(country, locale), {
      // Index files are immutable per deploy — let the browser cache them
      // aggressively and avoid revalidation round-trips.
      cache: "force-cache",
    })
    if (!res.ok) return null
    const data = (await res.json()) as SearchIndex
    if (data.version !== SEARCH_INDEX_VERSION) return null
    return data
  } catch {
    return null
  }
}

export function loadSearchIndex(
  country: string,
  locale: string
): Promise<SearchIndex | null> {
  const key = `${country}/${locale}`
  let entry = cache.get(key)
  if (!entry) {
    entry = fetchIndex(country, locale)
    cache.set(key, entry)
  }
  return entry
}

/**
 * Convenience wrapper: load (cached) and search in one call. Returns hits or
 * an empty array if the index couldn't be loaded — callers shouldn't need a
 * separate error state for "search file 404'd"; that's effectively a no-op.
 */
export async function runSearch(
  country: string,
  locale: string,
  query: string,
  options?: SearchOptions
): Promise<SearchHit[]> {
  const idx = await loadSearchIndex(country, locale)
  if (!idx) return []
  return search(idx.docs, query, options)
}

export type ResultGroup = {
  type: SearchDocType
  hits: SearchHit[]
}

export const RESULT_GROUP_ORDER: SearchDocType[] = [
  "page",
  "biomarker",
  "disease",
  "test",
  "insight",
  "faq",
  "location",
  "author",
  "career",
]

/** Bucket hits by type while preserving rank order within each bucket. */
export function groupHitsByType(hits: ReadonlyArray<SearchHit>): ResultGroup[] {
  const buckets = new Map<SearchDocType, SearchHit[]>()
  for (const hit of hits) {
    const list = buckets.get(hit.doc.type) ?? []
    list.push(hit)
    buckets.set(hit.doc.type, list)
  }
  const groups: ResultGroup[] = []
  for (const type of RESULT_GROUP_ORDER) {
    const list = buckets.get(type)
    if (list && list.length > 0) groups.push({ type, hits: list })
  }
  return groups
}

export type { SearchDoc }
